import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import { historyPageSchema, type FlowKind, type HistoryItem, type StandardStage } from './types';

// Historial y ajuste de estándares de producción (so=420 pestaña Estándares; so=340).
// La empresa activa va en el queryKey; un ajuste relee la matriz y el historial.

const BASE = '/api/v1/quality/production-standards';
const PAGE = 20;

export interface Work {
  documentTypeId: string;
  flowKind: FlowKind;
}

export interface HistoryPage {
  items: HistoryItem[];
  nextCursor: string | null;
}

export function useStandardHistory(
  work: Work | null,
  companyId: string,
): UseInfiniteQueryResult<InfiniteData<HistoryPage, string | null>> {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['time-study', companyId, 'standards-history', work],
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams({
        documentTypeId: work?.documentTypeId ?? '',
        flowKind: work?.flowKind ?? 'creacion',
        limit: String(PAGE),
      });
      if (pageParam !== null) {
        p.set('cursor', pageParam);
      }
      const parsed = historyPageSchema.parse(await api.get<unknown>(`${BASE}/history?${p}`));
      return { items: parsed.data, nextCursor: parsed.meta.next_cursor };
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: work !== null,
  });
}

export interface AdjustRequest extends Work {
  stages: Record<StandardStage, number | null>;
  reason: string;
}

export function useAdjustStandard(
  companyId: string,
): UseMutationResult<unknown, Error, AdjustRequest> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<unknown>(BASE, body),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['time-study', companyId, 'standards'] }),
        qc.invalidateQueries({ queryKey: ['time-study', companyId, 'standards-history'] }),
      ]),
  });
}
